define((require) => {
    "use strict";

    const $                         = require("jquery");
    const Backbone                  = require("backbone");
    const ChannelSetupManualView    = require("ui/publish/channelSetup/ChannelSetupManualView");
    const Browser                   = require("shared/services/Browser");
    const Clipboard                 = require("services/Clipboard");
    const Configuration             = require("shared/services/Configuration");
    const EditorEvents              = require("shared/services/EditorEvents");
    const ImprovedView              = require("editor/zbone/ImprovedView");
    const {Logger}                    = require("shared/util/Logger");
    const MessageNode               = require("services/messageNode/MessageNode");
    const MessageTypes              = require("ui/publish/MessageTypes");
    const Permissions               = require("shared/security/Permissions");
    const Storage                   = require("shared/services/Storage");
    const template                  = require("ui/publish/PublishView.tpl");
    const Tooltip                   = require("ui/common/Tooltip");
    const UrlUtil                   = require("shared/util/UrlUtil");
    const Window                    = require("shared/services/Window");
    const PublishUtil               = require("ui/publish/PublishUtil");

    const ADMIN_MODE_IDENTIFIER = "zmagsCreatorAdminMode"; // query param *or* session storage
    const LAST_ADDRESS_STORAGE_KEY = "publishLastAddress";
    const SINGLE_WINDOW_KEY = "publishSingleWindow";

    const log = new Logger("PublishView");

    const showSingleWindow = () => {
        // This could eventually also be based on user preferences (except IE where we have no choice)...
        return Browser.type.ie || Browser.type.edge || Storage.Local.get(Storage.Keys.PUBLISH_VIEW, SINGLE_WINDOW_KEY);
    };

    /**
     * The PublishView is responsible for display publish functionality to the user.
     *
     * @class PublishView
     */
    return ImprovedView.extend({

        /**
         * Prevent that beforeNavigate method is called more than once when moving to experience section, which will include the create and edit sub URLs.
         */
        _confirmed: false,

        _updateFromDnd: false,

        _channelSetupView: null,

        channelSetupViewClass: ChannelSetupManualView,

        _node: null,

        _toolbarWindow: null,

        _customerSiteWindow: null,

        _timer: null,

        _tooltip: null,

        template: template,

        className: "publish-view",

        templateHelpers() {
            const key = Browser.os.macOS ? "\u2318" : "Ctrl";
            const {bundleStrategy, channelConfigId} = this.options;

            return {
                allowedToPublish: Permissions.Privileges.PUBLISH.can(),
                button: key,
                snippet: PublishUtil.getSnippetUrl(bundleStrategy, channelConfigId)
            };
        },

        regions: {
            channelSetupRegion: "#channel-setup-region",
            publishPrepareLink: "#publish-prepare-link"
        },

        ui: {
            goToAddressForm: ".js-go-to-address-form",
            publishChannel: "#publish-channels",
            publishDetails: "#publish-details",
            publishPrepareLink: "#publish-prepare-link",
            urlInput: "#url-input"
        },

        events: {
            "click @ui.publishDetails": "selectSnippet",

            "click": "_clickHandler",

            "submit @ui.goToAddressForm"() {
                this._handleGoToAddress();

                // Prevent the default submit action.
                return false;
            }
        },

        getTitle() {
            return "Publish";
        },

        _clickHandler(event) {
            switch (event.target.id) {
                case "publish-prepare-link":
                    this.ui.publishPrepareLink.toggleClass("publish-details-open");
                    this.ui.publishDetails.slideToggle(250);
                    break;
                default:
                    this._closeTooltip();
            }
        },

        onRender() {
            const url = Storage.Local.get(Storage.Keys.PUBLISH_VIEW, LAST_ADDRESS_STORAGE_KEY);
            const {
                publishableCollection, publishableDescriptors, channelCollection, projectContext, hasUnPushedChannels
            } = this.options;

            this.ui.urlInput.val(this._getDomainURLName(url));
            this.ui.publishDetails.popover();

            this._channelSetupView = new this.channelSetupViewClass({
                publishableCollection,
                publishableDescriptors,
                collection: channelCollection, // FROM publishables
                projectContext,
                allowedToPublish: Permissions.Privileges.PUBLISH.can(),
                node: () => (this._node),
                showPublishButton: true,
                showCreateSlotField: true,
                hasUnPushedChannels
            });

            this.channelSetupRegion.show(this._channelSetupView);

            // Let's track when users copy the publish snippet - using the Clipboard to listen for
            // cross-browser 'copy' events.
            this.listenTo(Clipboard, "copy", function() {
                // If the text area containing the snippet currently has focus
                if (document.activeElement === this.$el.find("textarea#snippet").get(0)) {
                    EditorEvents.trigger(EditorEvents.constants.COPY_SNIPPET, {
                        triggerOrigin: "SnippetContainer",
                        projectId: this.options.projectContext && this.options.projectContext.project.get("id")
                    });
                }
            });
        },

        /**
         * Exposes the event source (most likely a collection) associated with the number of
         * items represented by  *this* dashboard sub view. The dashboard will use this for updating
         * the counting labels, by listening for relevant events.
         *
         * @return {Events[]} The event sources needed for the dashboard.
         *
         * @see DashboardView#showTab
         */
        getCountableEventSource() {
            return this.options.channelCollection;
        },

        selectSnippet() {
            const element = this.ui.publishDetails[0];
            let range;
            let selection;

            if (document.body.createTextRange) {
                range = document.body.createTextRange();
                range.moveToElementText(element);
                range.select();
            } else if (window.getSelection) {
                selection = window.getSelection();
                range = document.createRange();
                range.selectNodeContents(element);
                selection.removeAllRanges();
                selection.addRange(range);
            }
        },

        /**
         * Invoked when navigating away from *this* view - returns <tt>true</tt> if there are any unsaved changes.
         *
         * @return {Boolean} <tt>true</tt> if unsaved changes, <tt>false</tt> otherwise.
         */
        hasUnsavedChanges() {
            // Check the setup view for whether the model has changed...
            return this._channelSetupView.channelChangesInfo.hasAny();
        },

        /**
         * Handles domain naming on Publish WebSite URL.
         * @param {String} domainURL - Domain to be displayed.
         * @returns {String} - Current selected domain, empty string if "None".
         * @private
         */
        _getDomainURLName(domainURL) {
            return domainURL !== "None" ? domainURL : "";
        },

        _close() {
            this._closePopups();
            this._closeTooltip();

            // Remove admin mode identifier from storage - this has to be done like this, since channels.js is not
            // using the Storage abstraction, i.e. to ensure correct path...
            if (window.sessionStorage) {
                window.sessionStorage.removeItem(ADMIN_MODE_IDENTIFIER);
            }
        },

        _checkUrl(address) {
            if (this._toolbarWindow) {
                let error = 0;

                try {
                    // The following line will succeed if the client has *not* broken out of the frame, i.e. same
                    // domain. Or if the client has broken out of the iframe *and* if we are in a browser like Chrome
                    // with all web security disabled...
                    if (window.location.host.indexOf(this._toolbarWindow.location.host) === -1) {
                        error = 1;
                    }
                } catch (e1) { // SecurityError
                    error = 1;
                    // Fall-through!
                }

                if (!error) {
                    try {
                        // Try and see if we can access to the site iframe location - will be possible if set to
                        // "about:blank" in case the client site uses a "X-Frame-Options: Deny" header, otherwise we
                        // should get a SecurityError. This only works in Chrome (webkit), other browsers still set
                        // the url to "about:blank", but we still get a security error...
                        const iframe = this._toolbarWindow.document.getElementById("site");

                        if (iframe && iframe.contentWindow.location.protocol === "about:") { // === for Chrome with no web security
                            error = 2;
                        }
                    } catch (e2) { // *expected* SecurityError
                        // Fall-through!
                    }
                }

                if (error) {
                    this._close();
                    Storage.Local.unset(Storage.Keys.PUBLISH_VIEW, SINGLE_WINDOW_KEY);

                    let content = " appears that your website cannot be framed for Drag'n Drop publishing in a single window.<br><br>";
                    const msg = `It appears as if the client site '${address}'`;

                    if (error === 1) {
                        log.error(`${msg}has broken out of the publish Drag'n Drop frame!`);
                        content = `Unless you explicitly closed your window, it${content}`;
                    } else { // error === 2
                        log.error(`${msg}uses a 'X-Frame-Origins: Deny' header and cannot be framed!`);
                        content = `It${content}`;
                    }

                    if (Browser.type.ie || Browser.type.edge) {
                        content += `Unfortunately, the browser you are using does not support drag'n'drop between windows on different domains.<br><br>
                                   A solution could be to <a href="http://browsehappy.com/">upgrade your browser</a>
                                   or <a href="http://www.google.com/chromeframe/?redirect=true">activate Google Chrome Frame</a>
                                   to improve your experience.<br><br>
                                   However, you can still do manual publishing right here on this page.`;
                    } else {
                        content += `Please click "GO" again to use two windows for Drag'n Drop publishing.`;
                    }

                    if (Configuration.supportEmail) {
                        content += `<br><br>If you still have trouble, please contact <a href="mailto:${Configuration.supportEmail}">${Configuration.supportEmail}</a>.`;
                    }

                    this._tooltip = Tooltip.add(".js-go-to-address-button", {
                        title:   "Your website cannot be framed",
                        content: content,
                        trigger: "manual",
                        html: true
                    });
                    Tooltip.show(this._tooltip);
                }
            } else {
                this._closeTooltip();
            }
        },

        /**
         * Handles whether to open or not the drag 'n' drop publishing windows.
         * @private
         */
        _handleGoToAddress() {
            // If the channels have been changed, they should be saved first. If the user want to go to DnD
            // with unsaved channels, we want to ask for confirmation if they should be discarded or not.
            if (this._channelSetupView && this._channelSetupView.channelChangesInfo.hasAny()) {
                if (Browser.confirm("You are leaving Manual Publishing with unsaved changes.\n\nDo you want to discard your changes and continue?")) {
                    // Restore channel config state and go to Dnd.
                    this.restoreChannelSetupView();
                    this._goToAddress();
                }
                // else, if want to stay in the Publish tab, since user needs to save the changes before going to Dnd.
            } else {
                this._goToAddress();
            }
        },

        _goToAddress() {
            this._close();

            var old = Storage.Local.get(Storage.Keys.PUBLISH_VIEW, LAST_ADDRESS_STORAGE_KEY),
                address = this.ui.urlInput.val(),
                hostname;

            if (!address) {
                return;
            }

            if (old !== address) {
                Storage.Local.unset(Storage.Keys.PUBLISH_VIEW, SINGLE_WINDOW_KEY); // TODO: remove this when the user can actually choose
            }

            Storage.Local.update(Storage.Keys.PUBLISH_VIEW, LAST_ADDRESS_STORAGE_KEY, address);

            // Re-initialize node, since we can only create Origins upon construction for security reasons! We
            // have to use a regular expression to match against the host, since something like "dr.dk" would
            // probably mean "www.dr.dk" once the server redirects (and then the protocol as well) - but we have no
            // way of knowing this for individual sites at the time we create this node. We just make sure the
            // origins end with the same suffix...
            address = UrlUtil.prependHTTP(address);
            hostname = UrlUtil.location(address, "hostname");

            // Log config; ignore ping about channels being enabled, or it will flood the log...
            var logOptions = {log: Logger.level() >= Logger.LOG, ignore: MessageTypes.PAGE_IS_CHANNELS_ENABLED};

            this._node = new MessageNode({
                name: "publish", eksport: "__zmags.creator.node", log: logOptions, type: "root", origins: {allowed: hostname, callback: "err"}
            });

            var on = {},
                singleWindow = showSingleWindow();

            // Catch this early on, before a client site tries to break out. By first starting the timer here, we
            // ensure that pop-up blockers will not generate false positives...
            if (singleWindow) {
                on[MessageNode.Message.Actions.CHILD] = (message) => {
                    if (message.origin === "frame") {
                        // Check if sites like Tesco breaks out of the iframe. This is easily done, since when iframed, we
                        // can access the location of the opened window; when we no longer can (or if the url changes, i.e.
                        // on Chrome with no web security), the client site is misbehaving...
                        this._timer = setInterval(() => {
                            this._checkUrl(address);
                        }, 500);
                    }
                };
                // If we get a connection to either leaf node, we assume the client site has not broken out of the
                // frame. This seems to work fine, but it will in reality depend on the client site loading time vs
                // the toolbar loading time...
                on[MessageNode.Message.Actions.CHILDACK] = (message) => {
                    if (message.origin !== "frame") {
                        this._closeTooltip();
                    }
                };
            }

            // Close both pop-up windows and tooltip if either of them is closed via "Close-button" click...
            on[MessageNode.Message.Actions.LEAVE] = (message) => {
                if (message.data === "self") {
                    this._close();
                }
            };

            on[MessageTypes.NAVIGATE_TO] = (message) => {
                if (message.data.url.charAt(0) === "#") {
                    Backbone.history.navigate(message.data.url, true); // will call onClose for this view, which in turn calls _close()
                } else {
                    Window.open(message.data.url, message.data.target || "zmags_creator");
                }
            };

            // When we get this message with *this* node as a receiver, we know it is because of a save,
            // so we must update the channel setup view accordingly *and* make sure the global experience collection
            // is in sync with the new states from the drag-n-drop publishing - the latter is automatically handled by
            // channel setup view, and this view will render itself again once the channel config is fetched...
            on[MessageTypes.POPULATE_CHANNELS] = () => {
                this._updateFromDnd = true; // make sure we do not trigger update back
                this._channelSetupView
                    .refresh()
                    .always(() => {
                        this._updateFromDnd = false;
                    });
            };

            this._node.on(on, this);

            const $win = $(window);
            const toolbarWindowWidth = 380;
            const customerSiteWindowWidth = Math.round($win.width() - toolbarWindowWidth - 60);
            const windowHeight = Math.round($win.height() * 0.85);
            const left = 20 + ((window.screenX) ? window.screenX : window.screenLeft);
            const top = 120 + ((window.screenY) ? window.screenY : window.screenTop);

            // Check if we have project context. If we have, then open the toolbar with the project context
            const match = window.location.hash.match(/project\/\w+\//);
            const base = `#${match ? match[0] : ""}publishtoolbar/`;

            if (singleWindow) {
                this._toolbarWindow = Window.open(
                    this._addLogLevel(`${base}framed/${encodeURIComponent(address)}`),
                    "publishToolbarWindow",
                    `resizable=yes,scrollbars=yes,width=${customerSiteWindowWidth + toolbarWindowWidth},height=${windowHeight},top=${top},left=${left}`
                );
            } else {
                this._toolbarWindow = Window.open(
                    base + encodeURIComponent(address),
                    "publishToolbarWindow",
                    `scrollbars=yes,location=no,addressbar=no,width=${toolbarWindowWidth},height=${windowHeight},top=${top},left=${left + customerSiteWindowWidth + 20}`
                );
                this._customerSiteWindow = Window.open(
                    this._addLogLevel(UrlUtil.appendParam(address, `${ADMIN_MODE_IDENTIFIER}=true`)),
                    "customerSiteWindow",
                    `personalbar=yes,scrollbars=yes,menubar=yes,location=no,addressbar=no,toolbar=yes,width=${customerSiteWindowWidth},height=${windowHeight},top=${top},left=${left}`
                );
            }

            // Track the opening of DnD publish event
            const projectContext = this.options.projectContext;
            const eventProperties = {
                triggerOrigin: "GoToDragAndDropPublishingButton"
            };

            if (projectContext) {
                eventProperties.projectId = projectContext.project.get("id");
            }

            EditorEvents.trigger(EditorEvents.constants.OPEN_DRAG_AND_DROP_PUBLISH, eventProperties);
        },

        _addLogLevel(url) {
            // Since we are about to open on a customer window, pass through our logging settings...
            return UrlUtil.appendParam(url, `zmagsLog=${Logger.level()}`); // 0 = OFF, 1 = ERROR, 2 = LOG, 3 = VERBOSE
        },

        showSingleDragDropWindow(single) { // will always be single in IE!
            if (single) {
                Storage.Local.update(Storage.Keys.PUBLISH_VIEW, SINGLE_WINDOW_KEY, "single");
            } else {
                Storage.Local.unset(Storage.Keys.PUBLISH_VIEW, SINGLE_WINDOW_KEY);
            }
        },

        _closeTooltip() {
            if (this._tooltip) {
                Tooltip.remove(this._tooltip);
                this._tooltip = null;
            }

            if (this._timer) {
                clearInterval(this._timer);
            }
        },

        _closePopups() {
            let any = false;

            if (this._customerSiteWindow) {
                this._customerSiteWindow.close();
                any = true;
            }

            if (this._toolbarWindow) {
                this._toolbarWindow.close();
                any = true;
            }

            this._customerSiteWindow = null;
            this._toolbarWindow = null;

            // If no pop-ups, it might be because a reload have been done of this page; the actual opened
            // windows may still be open. If so, we try and force them close by sending a "close" action
            // to them...
            if (this._node) {
                if (!any) {
                    this._node.close(MessageNode.Message.Receivers.ALL);
                }

                this._node.close();
                this._node = null;
            }
        },

        onClose() {
            this._close();
        },

        /**
         * Restore channelsSetupView if the view already exists
         */
        restoreChannelSetupView() {
            if (this._channelSetupView) {
                this._channelSetupView.restore();
            }
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/publish/PublishView.js